import { TripStop } from '../../../../types/trip.types';

interface DetailRow {
  label: string;
  value: string;
}

export const getActivityStartTime = (activity: TripStop): string => {
  const formatTimeShort = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  switch (activity.stop_type) {
    case 'Flight':
      return formatTimeShort(activity.departure_date_time);

    case 'Hotel':
      return formatTimeShort(activity.check_in_date_time);

    case 'Ferry':
    case 'Cruise':
    case 'Rail':
    case 'Transportation':
      return formatTimeShort(activity.departure_date_time);

    case 'BorderCrossing':
      return formatTimeShort(activity.start_date_time);

    default:
      return formatTimeShort(activity.start_date_time);
  }
};

export const getStopStartDateTimeIso = (activity: TripStop): string | null => {
  switch (activity.stop_type) {
    case 'Flight':
      return activity.departure_date_time || null;
    case 'Hotel':
      return activity.check_in_date_time || null;
    case 'Ferry':
    case 'Cruise':
    case 'Rail':
    case 'Transportation':
      return activity.departure_date_time || null;
    case 'BorderCrossing':
      return activity.start_date_time || null;
    default:
      return activity.start_date_time || null;
  }
};

export const getStopEndDateTimeIso = (activity: TripStop): string | null => {
  switch (activity.stop_type) {
    case 'Flight':
      return activity.arrival_date_time || activity.departure_date_time || null;
    case 'Hotel':
      return activity.check_out_date_time || activity.check_in_date_time || null;
    case 'Ferry':
    case 'Cruise':
    case 'Rail':
    case 'Transportation':
      return activity.arrival_date_time || activity.departure_date_time || null;
    default: {
      const endDateTime = 'end_date_time' in activity ? activity.end_date_time : null;
      return endDateTime || activity.start_date_time || null;
    }
  }
};

export const formatActivityTime = (activity: TripStop): string => {
  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  };

  switch (activity.stop_type) {
    case 'Flight':
      const depDate = new Date(activity.departure_date_time);
      const arrDate = new Date(activity.arrival_date_time);

      if (depDate.toDateString() === arrDate.toDateString()) {
        const depTime = formatTime(activity.departure_date_time);
        const arrTime = formatTime(activity.arrival_date_time);
        return `${depTime} - ${arrTime}`;
      } else {
        const depTime = `${formatDate(activity.departure_date_time)} ${formatTime(activity.departure_date_time)}`;
        const arrTime = `${formatDate(activity.arrival_date_time)} ${formatTime(activity.arrival_date_time)}`;
        return `${depTime} - ${arrTime}`;
      }

    case 'Hotel':
      const checkInDate = new Date(activity.check_in_date_time);
      const checkOutDate = new Date(activity.check_out_date_time);

      if (checkInDate.toDateString() === checkOutDate.toDateString()) {
        const checkIn = formatTime(activity.check_in_date_time);
        const checkOut = formatTime(activity.check_out_date_time);
        return `Check-in: ${checkIn} • Check-out: ${checkOut}`;
      } else {
        const checkIn = `${formatDate(activity.check_in_date_time)} ${formatTime(activity.check_in_date_time)}`;
        const checkOut = `${formatDate(activity.check_out_date_time)} ${formatTime(activity.check_out_date_time)}`;
        return `Check-in: ${checkIn} • Check-out: ${checkOut}`;
      }

    case 'Ferry':
    case 'Cruise':
    case 'Rail':
    case 'Transportation':
      const depDate2 = new Date(activity.departure_date_time);
      const arrDate2 = new Date(activity.arrival_date_time);

      if (depDate2.toDateString() === arrDate2.toDateString()) {
        const depTime = formatTime(activity.departure_date_time);
        const arrTime = formatTime(activity.arrival_date_time);
        return `${depTime} - ${arrTime}`;
      } else {
        const depTime = `${formatDate(activity.departure_date_time)} ${formatTime(activity.departure_date_time)}`;
        const arrTime = `${formatDate(activity.arrival_date_time)} ${formatTime(activity.arrival_date_time)}`;
        return `${depTime} - ${arrTime}`;
      }

    case 'BorderCrossing':
      return formatTime(activity.start_date_time);

    default:
      const startTime = formatTime(activity.start_date_time);
      const endTime = formatTime(activity.end_date_time);
      return `${startTime} - ${endTime}`;
  }
};

export const formatActivityTitle = (activity: TripStop): string => {
  switch (activity.stop_type) {
    case 'Flight':
      return `${activity.flight_from} → ${activity.flight_to}`;

    case 'Hotel':
      return activity.name;

    case 'CarRental':
      return activity.name || 'Car Rental';

    case 'BorderCrossing':
      return `${activity.country_from} → ${activity.country_to}`;

    case 'Ferry':
    case 'Cruise':
    case 'Rail':
      return activity.name;

    case 'Transportation':
      return `${activity.vehicle_type} Transport`;

    case 'Parking':
    case 'Directions':
      return activity.place;

    case 'Activity':
    case 'Concert':
    case 'Theatre':
    case 'Meeting':
    case 'Restaurant':
    case 'Tour':
      return activity.name;

    case 'Shopping':
      return activity.name || 'Shopping';
  }
};

export const formatActivityDetails = (activity: TripStop): DetailRow[] => {
  const details: DetailRow[] = [];

  if (activity.notes) {
    details.push({ label: 'Notes', value: activity.notes });
  }

  if (activity.phone_number) {
    details.push({ label: 'Phone', value: activity.phone_number });
  }

  if (activity.email) {
    details.push({ label: 'Email', value: activity.email });
  }

  if (activity.link) {
    details.push({ label: 'Website', value: activity.link });
  }

  switch (activity.stop_type) {
    case 'Flight':
      if (activity.airline) {
        details.unshift({ label: 'Airline', value: activity.airline });
      }
      if (activity.flight_number) {
        details.unshift({ label: 'Flight Number', value: activity.flight_number });
      }
      if (activity.seat) {
        details.unshift({ label: 'Seat', value: activity.seat });
      }
      break;

    case 'Hotel':
      if (activity.address?.name) {
        details.unshift({ label: 'Address', value: activity.address.name });
      }
      if (activity.booking) {
        details.unshift({ label: 'Booking Number', value: activity.booking });
      }
      break;

    case 'CarRental':
      if (activity.transport_name) {
        details.unshift({ label: 'Vehicle', value: activity.transport_name });
      }
      if (activity.pick_up_address?.name) {
        details.unshift({ label: 'Pickup Location', value: activity.pick_up_address.name });
      }
      if (activity.drop_off_address?.name) {
        details.unshift({ label: 'Drop-off Location', value: activity.drop_off_address.name });
      }
      if (activity.booking) {
        details.unshift({ label: 'Booking Number', value: activity.booking });
      }
      break;

    case 'BorderCrossing':
      if (activity.border_checkpoint) {
        details.unshift({ label: 'Checkpoint', value: activity.border_checkpoint });
      }
      if (activity.address?.name) {
        details.unshift({ label: 'Address', value: activity.address.name });
      }
      break;

    case 'Parking':
      if (activity.parking_space) {
        details.unshift({ label: 'Parking Space', value: activity.parking_space });
      }
      if (activity.address?.name) {
        details.unshift({ label: 'Location', value: activity.address.name });
      }
      if (activity.booking) {
        details.unshift({ label: 'Booking Number', value: activity.booking });
      }
      break;

    case 'Directions':
      if (activity.address?.name) {
        details.unshift({ label: 'Address', value: activity.address.name });
      }
      if (activity.booking) {
        details.unshift({ label: 'Booking Number', value: activity.booking });
      }
      break;

    case 'Ferry':
      if (activity.carrier_name) {
        details.unshift({ label: 'Carrier', value: activity.carrier_name });
      }
      if (activity.location_from_name) {
        details.unshift({ label: 'From', value: activity.location_from_name });
      }
      if (activity.location_to_name) {
        details.unshift({ label: 'To', value: activity.location_to_name });
      }
      if (activity.seat) {
        details.unshift({ label: 'Seat', value: activity.seat });
      }
      if (activity.booking) {
        details.unshift({ label: 'Booking Number', value: activity.booking });
      }
      break;

    case 'Cruise':
      if (activity.transport_name) {
        details.unshift({ label: 'Ship Name', value: activity.transport_name });
      }
      if (activity.location_from_name) {
        details.unshift({ label: 'Starting Port', value: activity.location_from_name });
      }
      if (activity.location_to_name) {
        details.unshift({ label: 'Ending Port', value: activity.location_to_name });
      }
      if (activity.cabin) {
        details.unshift({ label: 'Cabin', value: activity.cabin });
      }
      if (activity.cabin_type) {
        details.unshift({ label: 'Cabin Type', value: activity.cabin_type });
      }
      if (activity.booking) {
        details.unshift({ label: 'Booking Number', value: activity.booking });
      }
      break;

    case 'Rail':
      if (activity.train) {
        details.unshift({ label: 'Train Number', value: activity.train });
      }
      if (activity.train_type) {
        details.unshift({ label: 'Train Type', value: activity.train_type });
      }
      if (activity.location_from_name) {
        details.unshift({ label: 'From Station', value: activity.location_from_name });
      }
      if (activity.location_to_name) {
        details.unshift({ label: 'To Station', value: activity.location_to_name });
      }
      if (activity.seat) {
        details.unshift({ label: 'Seat', value: activity.seat });
      }
      if (activity.seat_type) {
        details.unshift({ label: 'Class', value: activity.seat_type });
      }
      if (activity.booking) {
        details.unshift({ label: 'Booking Number', value: activity.booking });
      }
      break;

    case 'Transportation':
      if (activity.carrier_name) {
        details.unshift({ label: 'Carrier', value: activity.carrier_name });
      }
      if (activity.location_from_name) {
        details.unshift({ label: 'From', value: activity.location_from_name });
      }
      if (activity.location_to_name) {
        details.unshift({ label: 'To', value: activity.location_to_name });
      }
      if (activity.number_of_passengers) {
        details.unshift({ label: 'Passengers', value: activity.number_of_passengers.toString() });
      }
      if (activity.booking) {
        details.unshift({ label: 'Booking Number', value: activity.booking });
      }
      break;

    case 'Activity':
    case 'Concert':
    case 'Theatre':
    case 'Meeting':
    case 'Restaurant':
    case 'Tour':
      if (activity.place) {
        details.unshift({ label: 'Location', value: activity.place });
      }
      if (activity.address?.name) {
        details.unshift({ label: 'Address', value: activity.address.name });
      }
      if (activity.booking) {
        details.unshift({ label: 'Booking Number', value: activity.booking });
      }
      if ((activity.stop_type === 'Concert' || activity.stop_type === 'Theatre') && activity.seat) {
        details.unshift({ label: 'Seat', value: activity.seat });
      }
      break;

    case 'Shopping':
      if (activity.place) {
        details.unshift({ label: 'Location', value: activity.place });
      }
      if (activity.address?.name) {
        details.unshift({ label: 'Address', value: activity.address.name });
      }
      if (activity.booking) {
        details.unshift({ label: 'Booking Number', value: activity.booking });
      }
      break;
  }

  return details;
};
